import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext
} from 'react';
import deepmerge from 'deepmerge';

import { setFromSession } from 'wtb-utils';

const SepaContext = createContext();

const defaultState = {
  accounts: [],
  banks: [],
  mainBanks: [],
  bankInfo: null,
  isManualProcess: false
};

const SepaProvider = ({ children, initialState: state = {} }) => {
  const initialState = deepmerge(defaultState, state);

  const [ accounts, setAccounts ] = useState(initialState.accounts);
  const [ banks, setBanks ] = useState(initialState.banks);
  const [ mainBanks, setMainBanks ] = useState(initialState.mainBanks);
  const [ bankInfo, setBankInfo ] = useState(initialState.bankInfo);
  const [ isManualProcess, setIsManualProcess ] = useState(initialState.isManualProcess);

  const hasBankSelected = useCallback(
    () => bankInfo && bankInfo.bank && bankInfo.bank.name,
    [ bankInfo ]
  );
  const hasBankInfo = useCallback(
    () => bankInfo && bankInfo.iban && (bankInfo.bic || bankInfo.bank.bic),
    [ bankInfo ]
  );

  useEffect(() => {
    if (!bankInfo) return;

    setFromSession('bankInfo', JSON.stringify(bankInfo));
  }, [ bankInfo ]);

  const _setAccounts = (accs) => {
    const _accounts = accs.map((account) => ({
      id: account.id || `${account.iban}`,
      ...account
    }));

    setAccounts(_accounts);
  };

  const _setIsManualProcess = (value) => {
    setFromSession('isManualAccount', value);

    setIsManualProcess(value);
  };

  return (
    <SepaContext.Provider
      value={{
        accounts, setAccounts: _setAccounts,
        banks, setBanks,
        mainBanks, setMainBanks,
        bankInfo, setBankInfo,
        isManualProcess, setIsManualProcess: _setIsManualProcess,
        hasBankSelected,
        hasBankInfo
      }}
    >
      {children}
    </SepaContext.Provider>
  );
};

const SepaConsumer = SepaContext.Consumer;

const useSepaContext = () => {
  const context = useContext(SepaContext);

  if (context === undefined) {
    throw new Error('useSepaContext must be used within a SepaProvider');
  }

  return context;
};

export {
  SepaContext, SepaProvider, SepaConsumer, useSepaContext
};
